import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeft, ArrowUpRight, AlertTriangle, ShieldCheck, Tag, Coins, TrendingUp,
  Clock, Banknote, FileCheck2, Bookmark, Sparkles, Info, LogIn,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BrandLogo } from "@/components/BrandLogo";
import { BrandStack } from "@/components/BrandStack";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { format } from "@/lib/brand-tokens";
import {
  deals, getDealById, getBrandById, getValidationRuleById, dealDefaultFAQ,
} from "@/lib/mock-data";
import { parseRateChange } from "@/lib/dataAdapter";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

// PUB-03 — Public deal detail (preview-only).
// Mirrors the member detail page content but swaps the reserve sidebar for a
// sign-in / apply CTA panel. Renders inside StandaloneShell with the public
// navbar so it sits naturally next to the home page and Browse Deals.
export default function PublicDealDetailPage() {
  const { dealId } = useParams();
  const deal = getDealById(dealId) || deals.find((d) => d.status === "Published");
  const primaryCfg = (deal.brandConfigs || []).find(c => c.isPrimary) || (deal.brandConfigs || [])[0];
  const days = Math.max(0, Math.ceil((new Date(deal.deadline) - Date.now()) / 86_400_000));
  const closesSoon = days <= 3;
  const rateChange = parseRateChange(deal.internalNote);

  const faq = useMemo(() => (
    Array.isArray(deal.faqOverride) && deal.faqOverride.length ? deal.faqOverride : dealDefaultFAQ
  ), [deal.faqOverride]);

  const validationGroups = useMemo(() => {
    const rules = (deal.validationRuleIds || []).map(getValidationRuleById).filter(Boolean);
    return {
      mustPass:   rules.filter(r => r.severity === "reject"),
      shouldPass: rules.filter(r => r.severity !== "reject"),
    };
  }, [deal.validationRuleIds]);

  // Aggregate fill for hero strip
  const aggCapTotal = (deal.brandConfigs || []).reduce((a, c) => a + c.capTotal, 0);
  const aggCapUsed  = (deal.brandConfigs || []).reduce((a, c) => a + c.capUsed, 0);
  const aggFillPct  = aggCapTotal > 0 ? Math.round((aggCapUsed / aggCapTotal) * 100) : 0;
  const aggRemaining = aggCapTotal - aggCapUsed;

  // Headline rate
  const rates = (deal.brandConfigs || []).map(c => c.rate);
  const heroRate = deal.ratesUniform ? rates[0] : `${Math.min(...rates)}–${Math.max(...rates)}`;

  // OG / Twitter meta for shareable previews
  const brandNames = (deal.brandConfigs || []).map(c => getBrandById(c.brandId)?.name).filter(Boolean).join(" + ");
  const metaTitle = `${deal.name} · ${heroRate}% on ${brandNames || "gift cards"} — Gift Card Buyers Club`;
  const metaDescription = deal.tagline
    || `Members-only buying group buys ${brandNames || "gift cards"} at ${heroRate}%. ACH payout in 5–10 days. ${days > 0 ? `Closes in ${days} day${days === 1 ? "" : "s"}.` : "Closing now."}`;
  const metaImage = (deal.brandConfigs || []).map(c => getBrandById(c.brandId)?.logoUrl).find(Boolean);
  useDocumentMeta({
    title:       metaTitle,
    description: metaDescription,
    image:       metaImage,
    url:         typeof window !== "undefined" ? window.location.href : undefined,
    type:        "website",
  });

  return (
    <div data-testid="public-deal-detail-page">
      <div className="max-w-site mx-auto px-6 lg:px-12 py-9 space-y-6 pb-16">
        <Link to="/deals" className="inline-flex items-center gap-1.5 text-caption text-ink-subtle hover:text-ink">
          <ChevronLeft className="size-3.5" /> Browse all deals
        </Link>

        {/* ─── HERO ──────────────────────────────────────────────────────── */}
        <header
          data-testid="deal-hero"
          className="relative overflow-hidden bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white rounded-2xl px-7 py-8 sm:px-10 sm:py-10"
        >
          <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay"
               style={{ backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' seed='3'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")" }} />

          <div className="relative flex flex-wrap items-center justify-between gap-6">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-x-3 gap-y-1 flex-wrap">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-caption font-semibold uppercase tracking-wide">
                  <Sparkles className="size-3" />
                  <span>{deal.isMultiBrand ? "Multi-brand deal" : "Active buy"}</span>
                  {deal.isFeatured && <span className="ml-1 text-amber-200">· Featured</span>}
                </div>
                {deal.isMultiBrand && brandNames && (
                  <span className="text-caption text-white/80 font-medium">· {brandNames}</span>
                )}
              </div>
              <h1 className="font-brand mt-4 text-4xl sm:text-5xl font-bold leading-[1.08] break-words">
                {deal.name}
              </h1>
              {!deal.isMultiBrand && brandNames && (
                <p className="mt-3 text-white/80 text-body-sm sm:text-body">{brandNames}</p>
              )}
            </div>

            <div className="flex items-center gap-5 shrink-0 self-center">
              <div className="text-right">
                <div className="font-brand text-5xl sm:text-6xl font-bold leading-none tabular-nums">
                  {heroRate}<span className="text-2xl sm:text-3xl text-white/70 align-top ml-1">%</span>
                </div>
                <div className="mt-1 text-caption text-white/60 uppercase tracking-wide">
                  {deal.ratesUniform ? "of face value" : "rate range"}
                </div>
              </div>
              <BrandStack brandIds={deal.brandIds} size="lg" />
            </div>
          </div>

          <div className="relative mt-8 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 rounded-lg overflow-hidden border border-white/10">
            <HeroStat icon={<Clock className="size-3.5" />} label="Closes in" value={`${days} day${days === 1 ? "" : "s"}`} urgent={closesSoon} />
            <HeroStat icon={<Tag className="size-3.5" />} label="Face value range" value={`$${deal.minFace}–$${deal.maxFace}`} />
            <HeroStat icon={<ShieldCheck className="size-3.5" />} label={deal.isMultiBrand ? "Per-member (primary)" : "Per-member cap"} value={format.currency(primaryCfg?.perMemberCap || 0)} />
            <HeroStat icon={<Banknote className="size-3.5" />} label={deal.isMultiBrand ? "Total cap remaining (all brands)" : "Cap remaining"} value={format.currency(aggRemaining)} />
          </div>
        </header>

        {rateChange && rateChange.retroactive && (
          <div data-testid="rate-change-banner" className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-start gap-3">
            <TrendingUp className="size-5 text-emerald-700 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-body-sm font-semibold text-ink">
                Rate just bumped to <span className="tabular-nums">{rateChange.to}%</span>
              </div>
              <p className="mt-0.5 text-caption text-ink-muted leading-relaxed">
                Members holding existing reservations on this deal are being honoured at the new rate.
              </p>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* ─── MAIN COLUMN ────────────────────────────────────────────── */}
          <section className="lg:col-span-2 space-y-6">
            <ContentCard eyebrow="About" title="Why we're running this deal">
              {deal.whyBuying ? (
                <p className="text-body text-ink-muted leading-relaxed whitespace-pre-line">{deal.whyBuying}</p>
              ) : (
                <p className="text-body-sm text-ink-hint italic">No description yet.</p>
              )}
              {deal.publicNote && (
                <div className="mt-5 bg-brand-50/60 border border-brand-100 rounded-md p-4 flex items-start gap-3">
                  <Info className="size-4 text-brand-700 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-caption font-semibold text-brand-900 mb-0.5">From the desk</div>
                    <p className="text-caption text-ink-muted leading-relaxed">{deal.publicNote}</p>
                  </div>
                </div>
              )}
              <div className="mt-6 pt-5 border-t border-line">
                <div className="flex items-center justify-between text-caption mb-2">
                  <span className="text-ink-muted">{deal.isMultiBrand ? "Total cap across all brands" : "Total cap usage"}</span>
                  <span className="text-ink tabular-nums">
                    {format.currency(aggCapUsed)} used / {format.currency(aggCapTotal)} · {aggFillPct}%
                  </span>
                </div>
                <Progress value={aggFillPct} className="h-2" />
              </div>
            </ContentCard>

            {deal.isMultiBrand && (
              <ContentCard eyebrow="Per brand" title="Each brand has its own rate & cap">
                <p className="text-caption text-ink-subtle mb-5">
                  Multi-brand deals are made up of separate per-brand pools. As a member, you pick the brand when you reserve.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {deal.brandConfigs.map((cfg) => {
                    const b = getBrandById(cfg.brandId);
                    const cfgRemaining = cfg.capTotal - cfg.capUsed;
                    const cfgFillPct = cfg.capTotal > 0 ? Math.round((cfg.capUsed / cfg.capTotal) * 100) : 0;
                    return (
                      <div key={cfg.brandId} className="border border-line rounded-lg p-4 bg-white">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            {b && <BrandLogo brand={b} size="sm" />}
                            <div className="min-w-0">
                              <div className="text-body-sm font-semibold text-ink truncate">{b?.name || cfg.brandId}</div>
                              {cfg.isPrimary && <div className="text-[10px] font-bold uppercase tracking-wide text-brand-700">Primary brand</div>}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-brand text-2xl font-bold text-brand-700 leading-none tabular-nums">{cfg.rate}%</div>
                            <div className="text-caption text-ink-subtle">of face</div>
                          </div>
                        </div>
                        <dl className="mt-4 grid grid-cols-2 gap-y-2 text-caption">
                          <dt className="text-ink-hint">Face range</dt>
                          <dd className="text-ink font-semibold tabular-nums text-right">${cfg.minFace}–${cfg.maxFace}</dd>
                          <dt className="text-ink-hint">Per-member cap</dt>
                          <dd className="text-ink font-semibold tabular-nums text-right">{format.currency(cfg.perMemberCap)}</dd>
                        </dl>
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-caption mb-1">
                            <span className="text-ink-muted">Cap remaining</span>
                            <span className="text-ink font-semibold tabular-nums">{format.currency(cfgRemaining)} of {format.currency(cfg.capTotal)}</span>
                          </div>
                          <Progress value={cfgFillPct} className="h-1.5" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ContentCard>
            )}

            <ContentCard eyebrow="How it works" title="From reservation to payout">
              <ol className="mt-1 space-y-5">
                <Step n={1} icon={<Bookmark className="size-4" />} title="Reserve your share"
                      body={deal.isMultiBrand
                        ? "Pick a brand, then lock in your slot at that brand's rate. Each brand has its own cap."
                        : `Lock in your slot at ${primaryCfg?.rate}% rate.`} />
                <Step n={2} icon={<FileCheck2 className="size-4" />} title="Submit your cards"
                      body={`Paste card numbers and PINs before ${format.date(deal.deadline)}. We OCR-check each card against this deal's validation rules.`} />
                <Step n={3} icon={<ShieldCheck className="size-4" />} title="We validate & approve"
                      body="Approved cards lock your payout. Rejected cards are flagged with a reason and don't count against your reservation." />
                <Step n={4} icon={<Banknote className="size-4" />} title="ACH lands in your bank" last
                      body={`Payment is initiated on the deal's terms: ${deal.paymentTerms}. Funds typically land 1–2 business days after.`} />
              </ol>
            </ContentCard>

            <ContentCard eyebrow="Card requirements" title="What we'll check on each card">
              <p className="text-caption text-ink-subtle mb-5">
                Cards are checked against these rules at approval time. <span className="font-semibold text-ink">Must-pass</span> rules will reject the card; <span className="font-semibold text-ink">should-pass</span> rules flag for manual review.
              </p>
              {validationGroups.mustPass.length > 0 && (
                <RuleGroup heading="Must pass" tone="red" rules={validationGroups.mustPass} />
              )}
              {validationGroups.shouldPass.length > 0 && (
                <div className={validationGroups.mustPass.length > 0 ? "mt-6" : ""}>
                  <RuleGroup heading="Should pass (flagged on fail)" tone="amber" rules={validationGroups.shouldPass} />
                </div>
              )}
              {validationGroups.mustPass.length === 0 && validationGroups.shouldPass.length === 0 && (
                <p className="text-caption text-ink-hint italic">No specific rules set for this deal — standard checks apply.</p>
              )}
            </ContentCard>

            <ContentCard eyebrow="FAQ" title="Common questions">
              <Accordion type="single" collapsible className="-mx-2">
                {faq.map((item, i) => (
                  <AccordionItem key={i} value={`q-${i}`} className="border-line">
                    <AccordionTrigger className="px-2 hover:no-underline text-left text-body-sm font-semibold text-ink hover:text-brand-700 [&[data-state=open]>svg]:text-brand-700">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="px-2 text-caption text-ink-muted leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </ContentCard>
          </section>

          {/* ─── PUBLIC CTA SIDEBAR ────────────────────────────────────── */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-20 space-y-4" data-testid="public-cta-card">
              {/* Members-only callout */}
              <div className="bg-white border-2 border-brand-700 rounded-xl p-6">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-50 border border-brand-100 text-caption font-semibold uppercase tracking-wide text-brand-700 mb-3">
                  <Sparkles className="size-3" /> Members only
                </div>
                <h2 className="font-brand text-h2 text-ink">Reserve your share at {deal.ratesUniform ? `${primaryCfg?.rate}%` : heroRate + "%"}.</h2>
                <p className="mt-2 text-caption text-ink-muted leading-relaxed">
                  Reservations are open to active members. Membership is free and capped — approval is typically within 2 business days.
                </p>

                <div className="mt-5 space-y-2.5">
                  <Link to="/apply" className="block">
                    <Button data-testid="cta-apply-btn" className="w-full h-11 bg-brand-700 hover:bg-brand-800 text-white">
                      Apply for membership
                      <ArrowUpRight className="size-4 ml-2" strokeWidth={2.4} />
                    </Button>
                  </Link>
                  <Link to="/login" className="block">
                    <Button variant="outline" data-testid="cta-login-btn" className="w-full h-11">
                      <LogIn className="size-4 mr-1.5" /> I'm a member — sign in
                    </Button>
                  </Link>
                </div>

                <div className="mt-5 pt-4 border-t border-line text-caption text-ink-hint leading-relaxed">
                  Payment terms: <span className="text-ink-subtle font-medium">{deal.paymentTerms}</span>
                </div>
              </div>

              {/* Why join compressed teaser */}
              <div className="bg-surface-alt border border-line rounded-xl p-5">
                <div className="eyebrow mb-2">Why join?</div>
                <ul className="space-y-2.5 text-caption text-ink-muted">
                  <Bullet>No membership fees · no listing fees · no platform fees</Bullet>
                  <Bullet>Encrypted card numbers at rest with AWS KMS</Bullet>
                  <Bullet>ACH paid in 5–10 days from card approval</Bullet>
                  <Bullet>Closed group · every member is identity-verified</Bullet>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

// ─── Primitives (kept in-file; same look-and-feel as MemberDealDetailPage) ──

function ContentCard({ eyebrow, title, children }) {
  return (
    <section className="bg-white border border-line rounded-xl p-7">
      {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
      {title && <h2 className="font-brand text-h2 text-ink font-semibold mb-5">{title}</h2>}
      {children}
    </section>
  );
}
function HeroStat({ icon, label, value, urgent = false }) {
  return (
    <div className={`p-4 bg-brand-900/70 backdrop-blur-sm ${urgent ? "ring-1 ring-amber-300/40" : ""}`}>
      <div className="flex items-center gap-1.5 text-caption text-white/60">{icon}{label}</div>
      <div className={`mt-1 text-body font-bold tabular-nums ${urgent ? "text-amber-200" : "text-white"}`}>{value}</div>
    </div>
  );
}
function Step({ n, icon, title, body, last = false }) {
  return (
    <li className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="size-9 rounded-full bg-brand-50 border border-brand-100 text-brand-700 grid place-items-center font-brand font-bold tabular-nums">{n}</div>
        {!last && <div className="w-px flex-1 bg-line mt-1" />}
      </div>
      <div className="flex-1 pb-1">
        <div className="flex items-center gap-2 text-body-sm font-semibold text-ink">
          {icon && <span className="text-brand-700">{icon}</span>}{title}
        </div>
        <p className="mt-1 text-caption text-ink-muted leading-relaxed">{body}</p>
      </div>
    </li>
  );
}
function RuleGroup({ heading, tone, rules }) {
  const dotClass = tone === "red" ? "bg-red-500" : "bg-amber-500";
  const badgeTone = tone === "red" ? "danger" : "warning";
  return (
    <div>
      <div className="text-caption font-semibold text-ink-subtle uppercase tracking-wide mb-3">{heading}</div>
      <ul className="space-y-2.5">
        {rules.map((rule) => (
          <li key={rule.id} className="flex items-start gap-3 py-1.5">
            <span className={`mt-2 size-1.5 rounded-full ${dotClass} shrink-0`} />
            <div className="flex-1">
              <div className="text-body-sm text-ink font-medium">{rule.name}</div>
              <div className="text-caption text-ink-subtle leading-snug">{rule.description}</div>
            </div>
            <StatusBadge tone={badgeTone}>{rule.severity}</StatusBadge>
          </li>
        ))}
      </ul>
    </div>
  );
}
function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-1.5 size-1 rounded-full bg-brand-700 shrink-0" />
      <span>{children}</span>
    </li>
  );
}
